import api from "./api";
import type {
  GlAccount,
  FinanceDepartment,
  BankAccount,
  CashDeposit,
  CardSettlement,
  BankReconciliation,
  BankStatementBalance,
  FinanceParty,
  FinanceVoucher,
  FinanceVoucherDetail,
  FinanceVoucherList,
  VoucherPayload,
  PendingApproval,
  DashboardReport,
  ProfitLossReport,
  TrialBalanceReport,
  DayBookReport,
  CashBookReport,
  ReconciliationReport,
  RoomPerformanceReport,
  RestaurantPerformanceReport,
  FrontOfficeSettings,
  RestaurantSettings,
  InventorySettings,
  PostingQueueCounts,
  PostingQueueItem,
  ClosingReport,
  ApprovalThreshold,
  StockCategory,
  StockItem,
  StockItemPayload,
  StockMovement,
  StockUnit,
  RecipeLine,
  RecipeLinePayload,
  PurchasePayload,
  PurchaseDetail,
  PurchaseList,
  StockValuationReport,
  FoodCostReport,
  PurchaseRegisterReport,
  ApAgingReport,
  ArAgingReport,
  AnnualPnlReport,
  PayrollSettings,
  PayrollEmployee,
  PayrollEmployeeList,
  PayrollRunDetail,
  PayrollRunList,
  PayrollRunPayload,
  PayrollBuildResult,
  PayrollStatutory,
  PayrollRegisterReport,
  RemittanceKind,
} from "../types";

/**
 * Income & Expense Management API client.
 *
 * Every response is the repo-wide `{ success, data | message, error }`
 * envelope, so this wrapper just unwraps `data`. Failures surface as an
 * Error carrying `.status` and `.code` (see services/api.ts) — the UI uses
 * `.code` for `day_closed`, `invalid_state` and friends rather than
 * string-matching the message.
 */
async function unwrap<T>(promise: Promise<{ data?: T }>): Promise<T> {
  const res = await promise;
  return res.data as T;
}

async function unwrapList<T>(promise: Promise<{ data?: T[] }>): Promise<T[]> {
  const res = await promise;
  return (res.data as T[]) || [];
}

/**
 * The ageing endpoints speak snake_case (`as_of`, `terms_days`); the panel
 * speaks camelCase. The translation happens here so no caller can forget it,
 * and anything unset is left OUT rather than sent as `undefined` — otherwise
 * the request would carry `terms_days=undefined`, the server would reject it
 * as "not a whole number of days", and the report would never open.
 */
function toAgingParams(params: { asOf?: string; termsDays?: number }): Record<string, string | number> {
  const out: Record<string, string | number> = {};
  if (params.asOf) out.as_of = params.asOf;
  if (typeof params.termsDays === "number" && Number.isFinite(params.termsDays)) {
    out.terms_days = params.termsDays;
  }
  return out;
}

/**
 * Absolute URL for a stored media asset (`/uploads/db/:id`). The API client
 * is mounted on `/api/v1` but the file route lives at the server root, so the
 * prefix has to be stripped before joining.
 */
export function mediaUrl(path: string | null | undefined): string {
  if (!path) return "";
  if (/^https?:\/\//.test(path)) return path;
  const base = (
    process.env.REACT_APP_API_URL || "http://localhost:5000/api/v1"
  ).replace(/\/api\/v1\/?$/, "");
  return `${base}${path.startsWith("/") ? "" : "/"}${path}`;
}

export const financeService = {
  /* ------------------------------------------------------------
   * Master data
   * ------------------------------------------------------------ */

  getAccounts: async (includeInactive = false): Promise<GlAccount[]> =>
    unwrapList<GlAccount>(
      api.get("/finance/accounts", { include_inactive: includeInactive ? "true" : undefined }),
    ),

  createAccount: async (payload: Partial<GlAccount>): Promise<GlAccount> =>
    unwrap<GlAccount>(api.post("/finance/accounts", payload)),

  updateAccount: async (id: string, payload: Partial<GlAccount>): Promise<GlAccount> =>
    unwrap<GlAccount>(api.put(`/finance/accounts/${id}`, payload)),

  getDepartments: async (): Promise<FinanceDepartment[]> =>
    unwrapList<FinanceDepartment>(api.get("/finance/departments")),

  createDepartment: async (payload: { code: string; name: string }): Promise<FinanceDepartment> =>
    unwrap<FinanceDepartment>(api.post("/finance/departments", payload)),

  getBankAccounts: async (includeInactive = false): Promise<BankAccount[]> =>
    unwrapList<BankAccount>(
      api.get("/finance/bank-accounts", { include_inactive: includeInactive ? "true" : undefined }),
    ),

  createBankAccount: async (payload: Partial<BankAccount>): Promise<BankAccount> =>
    unwrap<BankAccount>(api.post("/finance/bank-accounts", payload)),

  updateBankAccount: async (id: string, payload: Partial<BankAccount>): Promise<BankAccount> =>
    unwrap<BankAccount>(api.put(`/finance/bank-accounts/${id}`, payload)),

  getParties: async (type?: "customer" | "supplier"): Promise<FinanceParty[]> =>
    unwrapList<FinanceParty>(api.get("/finance/parties", { type })),

  createParty: async (payload: Partial<FinanceParty>): Promise<FinanceParty> =>
    unwrap<FinanceParty>(api.post("/finance/parties", payload)),

  updateParty: async (id: string, payload: Partial<FinanceParty>): Promise<FinanceParty> =>
    unwrap<FinanceParty>(api.put(`/finance/parties/${id}`, payload)),

  /* ------------------------------------------------------------
   * Vouchers
   * ------------------------------------------------------------ */

  getVouchers: async (params: Record<string, any> = {}): Promise<FinanceVoucherList> =>
    unwrap<FinanceVoucherList>(api.get("/finance/vouchers", params)),

  getVoucher: async (id: string): Promise<FinanceVoucherDetail> =>
    unwrap<FinanceVoucherDetail>(api.get(`/finance/vouchers/${id}`)),

  createVoucher: async (payload: VoucherPayload): Promise<FinanceVoucher> =>
    unwrap<FinanceVoucher>(api.post("/finance/vouchers", payload)),

  updateVoucher: async (id: string, payload: Partial<VoucherPayload>): Promise<FinanceVoucher> =>
    unwrap<FinanceVoucher>(api.put(`/finance/vouchers/${id}`, payload)),

  deleteVoucher: async (id: string): Promise<void> => {
    await api.delete(`/finance/vouchers/${id}`);
  },

  submitVoucher: async (id: string): Promise<FinanceVoucher> =>
    unwrap<FinanceVoucher>(api.post(`/finance/vouchers/${id}/submit`)),

  approveVoucher: async (id: string, notes?: string): Promise<FinanceVoucher> =>
    unwrap<FinanceVoucher>(api.post(`/finance/vouchers/${id}/approve`, { notes })),

  rejectVoucher: async (id: string, notes: string): Promise<FinanceVoucher> =>
    unwrap<FinanceVoucher>(api.post(`/finance/vouchers/${id}/reject`, { notes })),

  payVoucher: async (id: string, bankAccountId: string): Promise<FinanceVoucher> =>
    unwrap<FinanceVoucher>(api.post(`/finance/vouchers/${id}/pay`, { bank_account_id: bankAccountId })),

  /** Collect a credit income: Dr tender · Cr 1100. The mirror of payVoucher. */
  receiveVoucher: async (id: string, bankAccountId: string): Promise<FinanceVoucher> =>
    unwrap<FinanceVoucher>(
      api.post(`/finance/vouchers/${id}/receive`, { bank_account_id: bankAccountId }),
    ),

  voidVoucher: async (id: string, reason: string): Promise<FinanceVoucher> =>
    unwrap<FinanceVoucher>(api.post(`/finance/vouchers/${id}/void`, { reason })),

  /* ------------------------------------------------------------
   * Attachments
   * ------------------------------------------------------------ */

  uploadAttachment: async (
    voucherId: string,
    file: File,
    kind: string,
    onProgress?: (pct: number) => void,
  ) => {
    const form = new FormData();
    form.append("file", file);
    form.append("kind", kind);
    return api.upload(`/finance/vouchers/${voucherId}/attachments`, form, (e) => {
      if (e?.total) onProgress?.(Math.round((e.loaded / e.total) * 100));
    });
  },

  deleteAttachment: async (attachmentId: string): Promise<void> => {
    await api.delete(`/finance/attachments/${attachmentId}`);
  },

  /* ------------------------------------------------------------
   * Approvals
   * ------------------------------------------------------------ */

  getPendingApprovals: async (): Promise<{
    rows: PendingApproval[];
    thresholds: ApprovalThreshold[];
  }> =>
    unwrap<{ rows: PendingApproval[]; thresholds: ApprovalThreshold[] }>(
      api.get("/finance/approvals/pending"),
    ),

  getApprovalSettings: async (): Promise<{ thresholds: ApprovalThreshold[] }> =>
    unwrap<{ thresholds: ApprovalThreshold[] }>(api.get("/finance/settings/approvals")),

  saveApprovalSettings: async (thresholds: ApprovalThreshold[]) =>
    unwrap<{ thresholds: ApprovalThreshold[] }>(
      api.put("/finance/settings/approvals", { thresholds }),
    ),

  /* ------------------------------------------------------------
   * Transfers
   * ------------------------------------------------------------ */

  createTransfer: async (payload: {
    business_date: string;
    from_bank_account_id: string;
    to_bank_account_id: string;
    amount: number;
    description: string;
    idempotency_key?: string;
  }) => unwrap<any>(api.post("/finance/transfers", payload)),

  /* ------------------------------------------------------------
   * Banking — cash deposits, card settlements, statement control
   * ------------------------------------------------------------ */

  getCashDeposits: async (params: {
    from?: string;
    to?: string;
    bank_account_id?: string;
    status?: string;
    limit?: number;
  } = {}): Promise<CashDeposit[]> =>
    unwrapList<CashDeposit>(api.get("/finance/cash-deposits", params)),

  createCashDeposit: async (payload: {
    business_date: string;
    cash_account_id: string;
    bank_account_id: string;
    amount: number;
    slip_reference: string;
    remarks?: string;
    idempotency_key: string;
  }): Promise<{
    deposit: CashDeposit;
    journal: any;
    already_posted: boolean;
  }> => unwrap<any>(api.post("/finance/cash-deposits", payload)),

  retryCashDeposit: async (id: string): Promise<{
    deposit: CashDeposit;
    journal: any;
    already_posted: boolean;
  }> => unwrap<any>(api.post(`/finance/cash-deposits/${id}/retry`)),

  clearCashDeposit: async (
    id: string,
    bankReference: string,
  ): Promise<CashDeposit> =>
    unwrap<CashDeposit>(
      api.post(`/finance/cash-deposits/${id}/clear`, {
        bank_reference: bankReference,
      }),
    ),

  getCardSettlements: async (params: {
    from?: string;
    to?: string;
    bank_account_id?: string;
    status?: string;
    limit?: number;
  } = {}): Promise<CardSettlement[]> =>
    unwrapList<CardSettlement>(api.get("/finance/card-settlements", params)),

  createCardSettlement: async (payload: {
    business_date: string;
    card_account_id: string;
    bank_account_id: string;
    gross_amount: number;
    fee_amount: number;
    provider?: string;
    settlement_reference: string;
    remarks?: string;
    idempotency_key: string;
  }): Promise<{
    settlement: CardSettlement;
    journal: any;
    already_posted: boolean;
  }> => unwrap<any>(api.post("/finance/card-settlements", payload)),

  retryCardSettlement: async (id: string): Promise<{
    settlement: CardSettlement;
    journal: any;
    already_posted: boolean;
  }> => unwrap<any>(api.post(`/finance/card-settlements/${id}/retry`)),

  clearCardSettlement: async (
    id: string,
    bankReference: string,
  ): Promise<CardSettlement> =>
    unwrap<CardSettlement>(
      api.post(`/finance/card-settlements/${id}/clear`, {
        bank_reference: bankReference,
      }),
    ),

  getBankReconciliation: async (params: {
    bank_account_id: string;
    as_of?: string;
  }): Promise<BankReconciliation> =>
    unwrap<BankReconciliation>(api.get("/finance/banking/reconciliation", params)),

  saveStatementBalance: async (payload: {
    bank_account_id: string;
    statement_date: string;
    closing_balance: number;
    statement_reference?: string;
    notes?: string;
  }): Promise<BankStatementBalance> =>
    unwrap<BankStatementBalance>(api.post("/finance/banking/statement-balances", payload)),

  getStatementBalances: async (
    bankAccountId: string,
    limit = 24,
  ): Promise<BankStatementBalance[]> =>
    unwrapList<BankStatementBalance>(
      api.get("/finance/banking/statement-balances", {
        bank_account_id: bankAccountId,
        limit,
      }),
    ),

  /* ------------------------------------------------------------
   * Reports
   * ------------------------------------------------------------ */

  getDashboard: async (params: { from?: string; to?: string; department_id?: string } = {}) =>
    unwrap<DashboardReport>(api.get("/finance/dashboard", params)),

  getProfitLoss: async (params: { from?: string; to?: string; department_id?: string } = {}) =>
    unwrap<ProfitLossReport>(api.get("/finance/profit-loss", params)),

  getTrialBalance: async (params: { from?: string; to?: string } = {}) =>
    unwrap<TrialBalanceReport>(api.get("/finance/trial-balance", params)),

  getDayBook: async (params: { date?: string; page?: number; limit?: number } = {}) =>
    unwrap<DayBookReport>(api.get("/finance/day-book", params)),

  getCashBook: async (params: { from?: string; to?: string } = {}) =>
    unwrap<CashBookReport>(api.get("/finance/cash-book", params)),

  getReconciliation: async (date: string) =>
    unwrap<ReconciliationReport>(api.get("/finance/reconciliation", { date })),

  getRoomPerformance: async (params: { startDate?: string; endDate?: string } = {}) =>
    unwrap<RoomPerformanceReport>(api.get("/finance/room-performance", params)),

  /** Restaurant / KOT performance — both funnels, with the source-vs-ledger control. */
  getRestaurantPerformance: async (params: { startDate?: string; endDate?: string } = {}) =>
    unwrap<RestaurantPerformanceReport>(api.get("/finance/restaurant-performance", params)),

  /* ------------------------------------------------------------
   * Front-office posting queue (Phase 2)
   *
   * A closed business date defers the journal instead of failing check-in /
   * check-out; this is what lets a finance user see and drain the backlog.
   * ------------------------------------------------------------ */

  getFrontOfficeSettings: async () =>
    unwrap<FrontOfficeSettings>(api.get("/finance/settings/front-office")),

  saveFrontOfficeSettings: async (cutover_date: string) =>
    unwrap<{ cutover_date: string }>(api.put("/finance/settings/front-office", { cutover_date })),

  /* ------------------------------------------------------------
   * Restaurant / walk-in services cutover (Phase 3)
   *
   * The guest funnel on a POS order rides the folio and is gated by the
   * front-office cutover; this date gates the other half — walk-in POS and
   * walk-in laundry, which settle straight to the ledger. `reporting_start` is
   * the later of the two, which is what the restaurant KPIs clamp to.
   * ------------------------------------------------------------ */

  getRestaurantSettings: async () =>
    unwrap<RestaurantSettings>(api.get("/finance/settings/restaurant")),

  saveRestaurantSettings: async (cutover_date: string) =>
    unwrap<{ cutover_date: string }>(api.put("/finance/settings/restaurant", { cutover_date })),

  getPostingQueue: async () =>
    unwrap<PostingQueueCounts & { items: PostingQueueItem[] }>(
      api.get("/finance/postings/queue"),
    ),

  sweepPostingQueue: async () =>
    unwrap<{ posted: number; failed: number; still_pending: number }>(
      api.post("/finance/postings/sweep", {}),
    ),

  /* ------------------------------------------------------------
   * Purchases & inventory (Phase 4)
   *
   * Money stays behind finance:* on the server; the cupboard itself is behind
   * inventory:*. Nothing here can reach GL 1200 by declaring itself — a
   * purchase invoice is built by this client from a document, and the server
   * forces the account and the document kind.
   * ------------------------------------------------------------ */

  getInventorySettings: async () =>
    unwrap<InventorySettings>(api.get("/finance/settings/inventory")),

  saveInventorySettings: async (cutover_date: string) =>
    unwrap<{ cutover_date: string }>(api.put("/finance/settings/inventory", { cutover_date })),

  /* ---- Categories ---- */

  getStockCategories: async (includeInactive = false): Promise<StockCategory[]> =>
    unwrapList<StockCategory>(
      api.get("/finance/stock-categories", {
        include_inactive: includeInactive ? "true" : undefined,
      }),
    ),

  createStockCategory: async (payload: { name: string; is_active?: boolean }): Promise<StockCategory> =>
    unwrap<StockCategory>(api.post("/finance/stock-categories", payload)),

  updateStockCategory: async (
    code: string,
    payload: { name?: string; is_active?: boolean },
  ): Promise<StockCategory> =>
    unwrap<StockCategory>(api.put(`/finance/stock-categories/${code}`, payload)),

  /* ---- Stock master ---- */

  getStockUnits: async (): Promise<StockUnit[]> =>
    unwrapList<StockUnit>(api.get("/finance/stock-units")),

  getStockItems: async (includeInactive = false): Promise<StockItem[]> =>
    unwrapList<StockItem>(
      api.get("/finance/stock-items", { include_inactive: includeInactive ? "true" : undefined }),
    ),

  getStockItem: async (id: string): Promise<StockItem> =>
    unwrap<StockItem>(api.get(`/finance/stock-items/${id}`)),

  createStockItem: async (payload: StockItemPayload): Promise<StockItem> =>
    unwrap<StockItem>(api.post("/finance/stock-items", payload)),

  updateStockItem: async (id: string, payload: Partial<StockItemPayload>): Promise<StockItem> =>
    unwrap<StockItem>(api.put(`/finance/stock-items/${id}`, payload)),

  deleteStockItem: async (
    id: string,
  ): Promise<{ deleted: boolean; deactivated: boolean }> =>
    unwrap<{ deleted: boolean; deactivated: boolean }>(api.delete(`/finance/stock-items/${id}`)),

  getStockMovements: async (params: Record<string, any> = {}): Promise<StockMovement[]> =>
    unwrapList<StockMovement>(api.get("/finance/stock-movements", params)),

  /* ---- Recipes ---- */

  getRecipes: async (menuItemId?: string): Promise<RecipeLine[]> =>
    unwrapList<RecipeLine>(
      api.get("/finance/inventory/recipes", { menu_item_id: menuItemId }),
    ),

  saveRecipe: async (menuItemId: string, lines: RecipeLinePayload[]) =>
    unwrap<RecipeLine[]>(api.put(`/finance/inventory/recipes/${menuItemId}`, { lines })),

  /* ---- Waste & counts (manual, so a closed day refuses them) ---- */

  recordWaste: async (payload: any) => unwrap<any>(api.post("/finance/inventory/waste", payload)),

  recordAdjustment: async (payload: any) =>
    unwrap<any>(api.post("/finance/inventory/adjustment", payload)),

  /* ---- Purchase invoices ---- */

  getPurchases: async (params: Record<string, any> = {}): Promise<PurchaseList> =>
    unwrap<PurchaseList>(api.get("/finance/purchases", params)),

  getPurchase: async (id: string): Promise<PurchaseDetail> =>
    unwrap<PurchaseDetail>(api.get(`/finance/purchases/${id}`)),

  /** A4 printable order form (PDF) for a purchase invoice. */
  getPurchaseOrderForm: async (id: string): Promise<Blob> =>
    api.getBlob(`/finance/purchases/${id}/order-form.pdf`),

  /**
   * Open a generated PDF in a new tab.
   *
   * Same popup-safe helper as `reservationPrintService`: the window is
   * opened immediately when this is called, then pointed at the blob URL.
   * Returns false when the browser blocked the window so the caller can
   * surface it and download the file instead.
   */
  openPdf: (blob: Blob, title: string): boolean => {
    const win = window.open("", "resort-pdf-viewer");
    if (!win) return false;

    try {
      win.opener = null;
      win.document.title = title;
      win.document.write(
        `<!doctype html><html><head><meta charset="utf-8"><title>${title}</title>` +
          `<style>body{margin:0;background:#f5f5f5;display:flex;align-items:center;justify-content:center;` +
          `height:100vh;font:14px system-ui,sans-serif;color:#666}</style></head>` +
          `<body><p>Loading document…</p></body></html>`,
      );
      win.document.close();
    } catch {
      // Cross-origin or already-navigated window — fall through to the URL.
    }

    const url = window.URL.createObjectURL(blob);
    win.location.href = url;
    win.onbeforeunload = () => window.URL.revokeObjectURL(url);
    return true;
  },

  downloadPdf: (blob: Blob, filename: string) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    a.remove();
  },

  createPurchase: async (payload: PurchasePayload): Promise<FinanceVoucher> =>
    unwrap<FinanceVoucher>(api.post("/finance/purchases", payload)),

  updatePurchase: async (id: string, payload: Partial<PurchasePayload>): Promise<FinanceVoucher> =>
    unwrap<FinanceVoucher>(api.put(`/finance/purchases/${id}`, payload)),

  deletePurchase: async (id: string): Promise<void> => {
    await api.delete(`/finance/purchases/${id}`);
  },

  submitPurchase: async (id: string): Promise<FinanceVoucher> =>
    unwrap<FinanceVoucher>(api.post(`/finance/purchases/${id}/submit`, {})),

  approvePurchase: async (id: string, notes?: string): Promise<FinanceVoucher> =>
    unwrap<FinanceVoucher>(api.post(`/finance/purchases/${id}/approve`, { notes })),

  rejectPurchase: async (id: string, notes: string): Promise<FinanceVoucher> =>
    unwrap<FinanceVoucher>(api.post(`/finance/purchases/${id}/reject`, { notes })),

  payPurchase: async (id: string, bankAccountId: string): Promise<FinanceVoucher> =>
    unwrap<FinanceVoucher>(api.post(`/finance/purchases/${id}/pay`, { bank_account_id: bankAccountId })),

  voidPurchase: async (id: string, reason: string): Promise<FinanceVoucher> =>
    unwrap<FinanceVoucher>(api.post(`/finance/purchases/${id}/void`, { reason })),

  /* ---- Inventory reports ---- */

  getStockValuation: async (date?: string) =>
    unwrap<StockValuationReport>(api.get("/finance/stock-valuation", date ? { date } : {})),

  getFoodCost: async (params: { from: string; to: string }) =>
    unwrap<FoodCostReport>(api.get("/finance/food-cost", params)),

  getPurchaseRegister: async (params: { from: string; to: string }) =>
    unwrap<PurchaseRegisterReport>(api.get("/finance/purchase-register", params)),

  /* ---- Ageing (Phase 6) ----
   *
   * `asOf` is optional so the report opens on today without the panel having
   * to invent a date the backend would have used anyway; `termsDays` is
   * passed through only when set, leaving the server's default of 30 to
   * speak for itself rather than restating it in every request.
   */

  getApAging: async (params: { asOf?: string; termsDays?: number } = {}) =>
    unwrap<ApAgingReport>(api.get("/finance/aging/payables", toAgingParams(params))),

  getArAging: async (params: { asOf?: string; termsDays?: number } = {}) =>
    unwrap<ArAgingReport>(api.get("/finance/aging/receivables", toAgingParams(params))),

  /**
   * Twelve months of income / expense / profit, the same departmentally, and
   * last year beside it. Omitting `year` asks the server for the current one
   * rather than restating it here, so the two can never disagree about what
   * "this year" is.
   */
  getAnnualPnl: async (params: { year?: number | string; departmentId?: string } = {}) => {
    const query: Record<string, string | number> = {};
    if (params.year !== undefined && params.year !== null && params.year !== "") {
      query.year = params.year;
    }
    if (params.departmentId) query.departmentId = params.departmentId;
    return unwrap<AnnualPnlReport>(api.get("/finance/analytics", query));
  },

  /* ------------------------------------------------------------
   * Payroll (Phase 5)
   *
   * Everything here — even reading an employee's salary — is gated by
   * payroll:* on the server. `finance:view` deliberately does not reach it:
   * the cashier is allowed to know what the resort spent on labour and not
   * allowed to know what any individual was paid.
   * ------------------------------------------------------------ */

  getPayrollRuns: async (params: Record<string, any> = {}): Promise<PayrollRunList> =>
    unwrap<PayrollRunList>(api.get("/finance/payroll", params)),

  getPayrollRun: async (id: string): Promise<PayrollRunDetail> =>
    unwrap<PayrollRunDetail>(api.get(`/finance/payroll/${id}`)),

  /** Without `lines` the server pays every active, configured employee. */
  createPayrollRun: async (
    payload: PayrollRunPayload & { payment_method: string },
  ): Promise<PayrollBuildResult> => unwrap<PayrollBuildResult>(api.post("/finance/payroll", payload)),

  updatePayrollRun: async (id: string, payload: PayrollRunPayload): Promise<PayrollRunDetail> =>
    unwrap<PayrollRunDetail>(api.put(`/finance/payroll/${id}`, payload)),

  deletePayrollRun: async (id: string): Promise<void> => {
    await api.delete(`/finance/payroll/${id}`);
  },

  submitPayrollRun: async (id: string): Promise<PayrollRunDetail> =>
    unwrap<PayrollRunDetail>(api.post(`/finance/payroll/${id}/submit`, {})),

  approvePayrollRun: async (id: string, notes?: string): Promise<PayrollRunDetail> =>
    unwrap<PayrollRunDetail>(api.post(`/finance/payroll/${id}/approve`, { notes })),

  rejectPayrollRun: async (id: string, notes: string): Promise<PayrollRunDetail> =>
    unwrap<PayrollRunDetail>(api.post(`/finance/payroll/${id}/reject`, { notes })),

  /** Settles the NET PAY sitting in 2010 — never the employer cost. */
  payPayrollRun: async (id: string, bankAccountId: string): Promise<PayrollRunDetail> =>
    unwrap<PayrollRunDetail>(
      api.post(`/finance/payroll/${id}/pay`, { bank_account_id: bankAccountId }),
    ),

  voidPayrollRun: async (id: string, reason: string): Promise<PayrollRunDetail> =>
    unwrap<PayrollRunDetail>(api.post(`/finance/payroll/${id}/void`, { reason })),

  /* ---- Employees ---- */

  getPayrollEmployees: async (taxYear?: number): Promise<PayrollEmployeeList> =>
    unwrap<PayrollEmployeeList>(
      api.get("/finance/payroll/employees", taxYear ? { tax_year: taxYear } : {}),
    ),

  savePayrollEmployee: async (
    id: string,
    payload: {
      basic_salary?: number | null;
      default_allowances?: number;
      finance_department_id?: string | null;
      tax_year?: number;
      opening_income?: number;
      opening_tax?: number;
    },
  ): Promise<PayrollEmployee> =>
    unwrap<PayrollEmployee>(api.put(`/finance/payroll/employees/${id}`, payload)),

  /* ---- Payslips ---- */

  /**
   * Server-rendered payslip PDF — every employee of the run, or one with
   * `staffId`. Figures come from the stored lines, so the slip equals what
   * was posted. Gated by payroll:view on the server, same as the register.
   */
  getPayslips: async (runId: string, staffId?: string): Promise<Blob> =>
    api.getBlob(
      `/finance/payroll/${runId}/payslips`,
      staffId ? { staff_id: staffId } : undefined,
    ),

  /* ---- Statutory ---- */

  getPayrollSettings: async (): Promise<PayrollSettings> =>
    unwrap<PayrollSettings>(api.get("/finance/payroll/settings")),

  savePayrollSettings: async (payload: Partial<PayrollSettings>): Promise<PayrollSettings> =>
    unwrap<PayrollSettings>(api.put("/finance/payroll/settings", payload)),

  getStatutory: async (params: { to?: string; from?: string } = {}): Promise<PayrollStatutory> =>
    unwrap<PayrollStatutory>(api.get("/finance/payroll/remittances", params)),

  remitStatutory: async (payload: {
    kind: RemittanceKind;
    amount: number;
    business_date: string;
    bank_account_id: string;
    memo?: string;
  }) => unwrap<any>(api.post("/finance/payroll/remittances", payload)),

  /* ---- Reports ---- */

  getPayrollRegister: async (params: { from: string; to: string }) =>
    unwrap<PayrollRegisterReport>(api.get("/finance/payroll/register", params)),

  /* ------------------------------------------------------------
   * Daily close
   * ------------------------------------------------------------ */

  getClosings: async (limit = 60) =>
    unwrapList<any>(api.get("/finance/closings", { limit })),

  getClosingReport: async (date: string) =>
    unwrap<ClosingReport>(api.get(`/finance/closings/${date}`)),

  closeDay: async (payload: {
    business_date: string;
    counted_cash?: number | null;
    notes?: string;
  }) => unwrap<any>(api.post("/finance/closings", payload)),

  reopenDay: async (date: string, reason?: string) =>
    unwrap<any>(api.post(`/finance/closings/${date}/reopen`, { reason })),

  /* ------------------------------------------------------------
   * Maintenance
   * ------------------------------------------------------------ */

  rebuildSnapshots: async (from: string, to: string) =>
    unwrap<any>(api.post("/finance/maintenance/rebuild-snapshots", { from, to })),
};

export default financeService;
